/**
 * geocodificacion.js
 * 
 * Servicio para convertir direcciones en coordenadas (latitud, longitud)
 * 
 * Qué hace:
 * - Toma una dirección en texto y la convierte en coordenadas geográficas
 * - Soporta múltiples proveedores: Google Maps, Mapbox, HERE, Nominatim
 * - Usa el proveedor seleccionado o Nominatim como respaldo
 * 
 * Qué recibe:
 * - direccion (string): Dirección en formato texto (ej: "Av. Corrientes 1234, Buenos Aires")
 * - opciones.proveedor (string): 'google' | 'mapbox' | 'here' | 'nominatim' | 'auto'
 * 
 * Qué devuelve:
 * - Promise que resuelve a un objeto: { lat: número, lng: número, direccion: string }
 * - Si hay error, lanza una excepción
 * 
 * Dónde modificarlo:
 * - Para cambiar el servicio de geocodificación
 * - Para agregar más información en la respuesta
 * - Para cambiar el formato de la respuesta
 */

// Nota: Los servicios se importan dinámicamente solo cuando es necesario

/**
 * Convierte una dirección en texto a coordenadas geográficas
 * Intenta usar Google Maps API primero (si está configurada), luego Nominatim como respaldo
 * 
 * @param {string} direccion - Dirección completa a geocodificar (debe incluir ciudad, provincia, etc.)
 * @param {Object} opciones - Opciones adicionales para mejorar la precisión
 * @param {string} opciones.proveedor - 'google' | 'mapbox' | 'here' | 'nominatim' | 'auto' (default: 'auto')
 * @param {Array} opciones.viewbox - [minLng, minLat, maxLng, maxLat] para limitar búsqueda a un área (solo Nominatim)
 * @param {string} opciones.pais - País para limitar búsqueda (código ISO)
 * @returns {Promise<{lat: number, lng: number, direccion: string, importancia: number}>} Coordenadas y dirección formateada
 */
export async function geocodificar(direccion, opciones = {}) {
  try {
    // Validar que la dirección no esté vacía
    if (!direccion || direccion.trim() === '') {
      throw new Error('La dirección no puede estar vacía')
    }

    // Usar la dirección tal como la ingresó el usuario (sin agregar contexto automático)
    const direccionCompleta = direccion.trim()

    // Determinar qué proveedor usar
    const proveedor = opciones.proveedor || 'auto'

    // Si el proveedor es 'auto', intentar en orden de preferencia
    if (proveedor === 'auto') {
      // Intentar Google Maps primero
      if (import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
        try {
          const { geocodificarGoogle } = await import('./geocodificacionGoogle.js')
          const resultado = await geocodificarGoogle(direccionCompleta)
          return formatearResultado(resultado, 'google')
        } catch (error) {
          console.warn('Google Maps falló, intentando siguiente proveedor:', error.message)
        }
      }

      // Intentar Mapbox
      if (import.meta.env.VITE_MAPBOX_ACCESS_TOKEN) {
        try {
          const { geocodificarMapbox } = await import('./geocodificacionMapbox.js')
          const resultado = await geocodificarMapbox(direccionCompleta)
          return formatearResultado(resultado, 'mapbox')
        } catch (error) {
          console.warn('Mapbox falló, intentando siguiente proveedor:', error.message)
        }
      }

      // Intentar HERE
      if (import.meta.env.VITE_HERE_API_KEY) {
        try {
          const { geocodificarHere } = await import('./geocodificacionHere.js')
          const resultado = await geocodificarHere(direccionCompleta)
          return formatearResultado(resultado, 'here')
        } catch (error) {
          console.warn('HERE falló, usando Nominatim como respaldo:', error.message)
        }
      }
    } else {
      // Usar el proveedor específico solicitado
      try {
        switch (proveedor) {
          case 'google':
            if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
              throw new Error('Google Maps API key no configurada')
            }
            const { geocodificarGoogle } = await import('./geocodificacionGoogle.js')
            const resultadoGoogle = await geocodificarGoogle(direccionCompleta)
            return formatearResultado(resultadoGoogle, 'google')

          case 'mapbox':
            if (!import.meta.env.VITE_MAPBOX_ACCESS_TOKEN) {
              throw new Error('Mapbox access token no configurado')
            }
            const { geocodificarMapbox } = await import('./geocodificacionMapbox.js')
            const resultadoMapbox = await geocodificarMapbox(direccionCompleta)
            return formatearResultado(resultadoMapbox, 'mapbox')

          case 'here':
            if (!import.meta.env.VITE_HERE_API_KEY) {
              throw new Error('HERE API key no configurada')
            }
            const { geocodificarHere } = await import('./geocodificacionHere.js')
            const resultadoHere = await geocodificarHere(direccionCompleta)
            return formatearResultado(resultadoHere, 'here')

          case 'nominatim':
            // Continuar con Nominatim (código más abajo)
            break

          default:
            throw new Error(`Proveedor desconocido: ${proveedor}`)
        }
      } catch (error) {
        // Si el proveedor específico falla y no es Nominatim, intentar Nominatim como respaldo
        if (proveedor !== 'nominatim') {
          console.warn(`${proveedor} falló, usando Nominatim como respaldo:`, error.message)
        } else {
          throw error
        }
      }
    }

    // Usar Nominatim como servicio principal o respaldo

    // Construir la URL con parámetros mejorados para mayor precisión
    const params = new URLSearchParams({
      format: 'json',
      q: direccionCompleta,
      limit: '5', // Obtener más resultados para elegir el mejor
      addressdetails: '1', // Incluir detalles de la dirección
      extratags: '1', // Incluir tags adicionales
      namedetails: '1' // Incluir nombres alternativos
    })

    // Si se proporciona un viewbox, limitar la búsqueda a esa área
    // Esto mejora mucho la precisión al evitar resultados en otras ciudades
    if (opciones.viewbox && Array.isArray(opciones.viewbox) && opciones.viewbox.length === 4) {
      params.append('viewbox', opciones.viewbox.join(','))
      params.append('bounded', '1') // Solo buscar dentro del viewbox
    }

    // Si se proporciona código de país, limitar la búsqueda a ese país
    if (opciones.pais && typeof opciones.pais === 'string' && opciones.pais.length === 2) {
      params.append('countrycodes', opciones.pais.toLowerCase())
    }

    const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`
    
    // Realizar la petición HTTP
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'TuRutaTrack/1.0' // Requerido por Nominatim
      }
    })

    // Verificar que la respuesta sea exitosa
    if (!response.ok) {
      throw new Error(`Error en la geocodificación: ${response.status}`)
    }

    // Convertir la respuesta a JSON
    const data = await response.json()

    // Verificar que se encontró al menos un resultado
    if (!data || data.length === 0) {
      throw new Error(`No se encontró la dirección: ${direccion}`)
    }

    // Seleccionar el mejor resultado basado en importancia y tipo
    // Los resultados con mayor importancia y tipo "house" o "building" son más precisos
    let mejorResultado = data[0]
    let mejorPuntuacion = 0

    data.forEach(resultado => {
      const importancia = parseFloat(resultado.importance || 0)
      const tipo = resultado.type || ''
      const clase = resultado.class || ''
      
      // Priorizar resultados que sean casas o edificios
      let puntuacion = importancia
      if (clase === 'highway' && tipo === 'residential') {
        // Calles residenciales tienen buena puntuación
        puntuacion += 0.1
      } else if (clase === 'building' || tipo === 'house' || tipo === 'building') {
        // Edificios y casas tienen la mejor puntuación
        puntuacion += 0.3
      } else if (clase === 'place' && (tipo === 'house_number' || tipo === 'address')) {
        // Números de casa y direcciones específicas
        puntuacion += 0.2
      }

      if (puntuacion > mejorPuntuacion) {
        mejorPuntuacion = puntuacion
        mejorResultado = resultado
      }
    })

    const lat = parseFloat(mejorResultado.lat)
    const lng = parseFloat(mejorResultado.lon)

    // Verificar que las coordenadas sean válidas
    if (isNaN(lat) || isNaN(lng)) {
      throw new Error('Coordenadas inválidas recibidas del servicio')
    }

    // Retornar las coordenadas y la dirección formateada
    return {
      lat,
      lng,
      direccion: mejorResultado.display_name || direccion,
      importancia: mejorResultado.importance || 0,
      tipo: mejorResultado.type || '',
      clase: mejorResultado.class || '',
      // Incluir todos los resultados para que el usuario pueda elegir si es necesario
      resultadosAlternativos: data.slice(1, 3) // Primeros 2 alternativos
    }
  } catch (error) {
    // Re-lanzar el error con un mensaje más claro
    throw new Error(`Error al geocodificar "${direccion}": ${error.message}`)
  }
}

/**
 * Geocodifica múltiples direcciones
 * Usa Google Maps si está disponible (más rápido), sino usa Nominatim con delays
 * 
 * @param {string[]} direcciones - Array de direcciones a geocodificar
 * @param {Object} opciones - Opciones adicionales (viewbox, pais, forzarNominatim)
 * @returns {Promise<Array<{lat: number, lng: number, direccion: string}>>} Array de coordenadas
 */
export async function geocodificarMultiples(direcciones, opciones = {}) {
  const proveedor = opciones.proveedor || 'auto'

  // Si hay un proveedor específico configurado, usarlo directamente
  if (proveedor !== 'auto' && proveedor !== 'nominatim') {
    try {
      switch (proveedor) {
        case 'google':
          if (import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
            const { geocodificarMultiplesGoogle } = await import('./geocodificacionGoogle.js')
            return await geocodificarMultiplesGoogle(direcciones)
          }
          break

        case 'mapbox':
          if (import.meta.env.VITE_MAPBOX_ACCESS_TOKEN) {
            const { geocodificarMultiplesMapbox } = await import('./geocodificacionMapbox.js')
            return await geocodificarMultiplesMapbox(direcciones)
          }
          break

        case 'here':
          if (import.meta.env.VITE_HERE_API_KEY) {
            const { geocodificarMultiplesHere } = await import('./geocodificacionHere.js')
            return await geocodificarMultiplesHere(direcciones)
          }
          break
      }
    } catch (error) {
      console.warn(`Error usando ${proveedor}, usando método individual:`, error)
      // Continuar con método individual...
    }
  }

  // Usar método individual (funciona con cualquier proveedor, incluyendo 'auto')
  const resultados = []
  
  for (let i = 0; i < direcciones.length; i++) {
    const resultado = await geocodificar(direcciones[i], opciones)
    resultados.push(resultado)
    
    // Delay según el proveedor usado
    const delay = resultado.proveedor === 'nominatim' ? 1100 : 100
    if (i < direcciones.length - 1) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  return resultados
}

